import { Component } from '@angular/core';
import { AnyoComponent } from 'projects/app-core/src/app/models/AnyoComponent';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';

import {
  AdminAssignExpertToReferralError,
  GoalsService,
  UnassignedReferralsGridResponseData,
} from '../../../services/goals.service';
import { TherapistService } from '../../../services/therapist.service';
import {
  TherapistFilter,
  TherapistGridRequest,
} from '../../../models/therapistGridRequest';

const ASSIGN_FAILED_TEXT = 'Could not assign the expert';

const ASSIGN_ERROR_TEXT: Record<AdminAssignExpertToReferralError, string> = {
  [AdminAssignExpertToReferralError.REFERRAL_NOT_FOUND]:
    'This referral no longer exists. The list has been reloaded.',
  [AdminAssignExpertToReferralError.REFERRAL_NOT_AWAITING_EXPERT]:
    'This referral was already handled. The list has been reloaded.',
  [AdminAssignExpertToReferralError.REFERRAL_ALREADY_ASSIGNED]:
    'This referral was already handled. The list has been reloaded.',
  [AdminAssignExpertToReferralError.EXPERT_NOT_ELIGIBLE]:
    'This expert is not an active Goals expert',
  [AdminAssignExpertToReferralError.EXPERT_MISSING_GOALS_ROLE]:
    'Open this expert in the experts list and save them again to grant the Goals role',
  [AdminAssignExpertToReferralError.EXPERT_ROLE_UNVERIFIABLE]:
    "Could not verify the expert's Goals role, try again",
};

/** Referrals reload when the row is stale (someone else moved it) so it cannot be acted on twice. */
const STALE_REFERRAL_ERRORS = [
  AdminAssignExpertToReferralError.REFERRAL_NOT_FOUND,
  AdminAssignExpertToReferralError.REFERRAL_NOT_AWAITING_EXPERT,
  AdminAssignExpertToReferralError.REFERRAL_ALREADY_ASSIGNED,
];

/**
 * Ops listing of Goals referrals awaiting an expert that have none, with a per-row assign action.
 * Experts no longer see unassigned referrals, so this is the only way one reaches an expert. Rows
 * deliberately carry no member identity; a referral is told apart by pillar, reason and age.
 * The expert picker mirrors goals-panel.component.ts's change-expert modal.
 */
@Component({
  selector: 'app-unassigned-referrals',
  templateUrl: './unassigned-referrals.component.html',
})
export class UnassignedReferralsComponent extends AnyoComponent {
  constructor(
    private goalsSvc: GoalsService,
    private therapistService: TherapistService,
    auth: AuthService,
    private toastrService: ToastService,
  ) {
    super(auth);
  }

  tableLoading = false;
  page = 1;
  pageSize = 20;
  totalRecords = 0;
  pageSizeOptions = [20, 50, 100];
  data: UnassignedReferralsGridResponseData[] = [];

  actionLoading = false;
  isAssignModalOpen = false;
  assignTargetRow: UnassignedReferralsGridResponseData | null = null;
  expertOptions: { id: string; name: string }[] = [];
  expertOptionsLoading = false;
  selectedNewExpertId: string | null = null;

  private fetchRequestId = 0;

  async ready() {
    if (!this.user) return;
    this.fetch();
  }

  fetch(): void {
    this.tableLoading = true;
    const requestId = ++this.fetchRequestId;
    this.goalsSvc
      .getUnassignedReferralsGrid({
        page: this.page,
        pageSize: this.pageSize,
        export: false,
      })
      .subscribe({
        next: (res) => {
          if (requestId !== this.fetchRequestId) return;
          // Assigning the last row of the last page leaves a page past the end: show the first page.
          if (!res.data.length && this.page > 1 && res.totalRecords > 0) {
            this.page = 1;
            this.fetch();
            return;
          }
          this.data = res.data;
          this.totalRecords = res.totalRecords;
          this.pageSize = res.pageSize;
          this.tableLoading = false;
        },
        error: (err) => {
          if (requestId !== this.fetchRequestId) return;
          this.tableLoading = false;
          // Stale rows would stay actionable if the reload after an assignment failed.
          this.data = [];
          this.totalRecords = 0;
          const errorBody = err.error as IAnyoError;
          this.toastrService.showAnyoErrorToast(
            errorBody?.description ?? 'Failed to load referrals',
          );
        },
      });
  }

  nzPageIndexChange(p: number): void {
    this.page = p;
    this.fetch();
  }

  nzPageSizeChange(s: number): void {
    this.pageSize = s;
    this.fetch();
  }

  openAssignModal(row: UnassignedReferralsGridResponseData): void {
    this.assignTargetRow = row;
    this.selectedNewExpertId = null;
    this.isAssignModalOpen = true;
    this.expertOptionsLoading = true;
    const req: TherapistGridRequest = {
      page: 1,
      pageSize: 200,
      export: false,
      filters: {
        [TherapistFilter.GOALS_EXPERT]: ['true'],
        [TherapistFilter.TEST_THERAPIST]: ['false'],
        [TherapistFilter.THERAPIST_RATING]: undefined,
        [TherapistFilter.THERAPIST_STATUS]: undefined,
      },
    };
    this.therapistService.listTherapist(req).subscribe({
      next: (res) => {
        this.expertOptions = res.data
          .filter((t) => !!t.id)
          .map((t) => ({
            id: t.id as string,
            name: `${t.firstName} ${t.lastName}`.trim(),
          }));
        this.expertOptionsLoading = false;
      },
      error: () => {
        this.expertOptionsLoading = false;
        this.toastrService.showAnyoErrorToast('Failed to load experts');
      },
    });
  }

  confirmAssign(): void {
    if (
      !this.assignTargetRow ||
      !this.selectedNewExpertId ||
      this.actionLoading
    ) {
      return;
    }
    this.actionLoading = true;
    this.goalsSvc
      .assignExpertToReferral(
        this.assignTargetRow.referralId,
        this.selectedNewExpertId,
      )
      .subscribe({
        next: (res) => {
          this.actionLoading = false;
          if (!res.success) {
            const code = res.error;
            this.toastrService.showAnyoErrorToast(
              (code && ASSIGN_ERROR_TEXT[code]) ?? ASSIGN_FAILED_TEXT,
            );
            if (code && STALE_REFERRAL_ERRORS.includes(code)) {
              this.isAssignModalOpen = false;
              this.fetch();
            }
            return;
          }
          this.isAssignModalOpen = false;
          if (res.chatCreated === false) {
            this.toastrService.showAnyoErrorToast(
              'Expert assigned, but the chat with the member could not be created',
            );
          } else {
            this.toastrService.showSuccess('Expert assigned successfully');
          }
          this.fetch();
        },
        error: (err) => {
          this.actionLoading = false;
          this.isAssignModalOpen = false;
          const errorBody = err.error as IAnyoError;
          this.toastrService.showAnyoErrorToast(
            errorBody?.description ?? ASSIGN_FAILED_TEXT,
          );
        },
      });
  }
}
